import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, LogOut, ArrowRight, Ruler, Palette, ShoppingBag, Bookmark, Loader2, Wand2, Users } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";

const TIER_ACCENT = {
  "Exceptional": "#04471C",
  "Excellent": "#0A3B85",
  "Good": "#C19A5B",
  "Situational": "#660018",
  "Requires Careful Styling": "#2B2B2B",
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState("");
  const [genLoading, setGenLoading] = useState(false);

  useEffect(() => {
    api.get("/dashboard")
      .then(({ data }) => {
        setData(data);
        setSummary(data.profile?.style_summary || "");
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const generateSummary = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post("/profile/summary");
      setSummary(data.style_summary);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGenLoading(false);
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sand">
        <Loader2 className="w-7 h-7 animate-spin text-emerald" strokeWidth={1.5} />
      </div>
    );
  }

  const prof = data.profile;
  const palette = prof.color?.palette;
  const proportions = prof.proportions;
  const complete = prof.completeness;
  const incomplete = complete < 100;

  return (
    <div className="min-h-screen bg-sand text-ink">
      <header className="px-6 sm:px-10 lg:px-16 py-6 flex items-center justify-between border-b border-ink/10">
        <div className="font-serif text-2xl">Élanora</div>
        <div className="flex items-center gap-6">
          {user?.role === "admin" && (
            <button onClick={() => navigate("/admin")} data-testid="admin-link"
              className="inline-flex items-center gap-2 text-sm text-ink/60 hover:text-ink transition-colors duration-300">
              <Users className="w-4 h-4" strokeWidth={1.5} /> Waitlist
            </button>
          )}
          <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn"
            className="inline-flex items-center gap-2 text-sm text-ink/60 hover:text-ink transition-colors duration-300">
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-14">
        {/* Greeting */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="eyebrow mb-3">Your styling studio</p>
          <h1 className="text-5xl sm:text-6xl tracking-tight" data-testid="dashboard-greeting">
            Hello, {data.name}.
          </h1>
        </motion.div>

        {/* Completeness */}
        <div className="mt-10 bg-[#F2EFEB] border border-ink/10 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-5" data-testid="completeness-card">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-ink/60">Profile completeness</span>
              <span className="text-sm font-medium">{complete}%</span>
            </div>
            <div className="h-[6px] bg-ink/10 rounded-full overflow-hidden">
              <div className="h-full bg-emerald transition-all duration-700" style={{ width: `${complete}%` }} />
            </div>
          </div>
          {incomplete && (
            <button onClick={() => navigate("/onboarding")} data-testid="finish-profile-btn"
              className="inline-flex items-center gap-2 rounded-full bg-emerald text-white px-6 py-3 text-sm hover:bg-emerald/90 transition-colors duration-300 whitespace-nowrap">
              Finish your profile <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          )}
        </div>

        {/* Today's colors */}
        {data.todays_colors?.length > 0 && (
          <section className="mt-16">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="w-5 h-5 text-emerald" strokeWidth={1.5} />
              <h2 className="text-3xl tracking-tight">Today's colors</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {data.todays_colors.map((c) => (
                <div key={c.name} className="border border-ink/10 rounded-[2px] overflow-hidden" data-testid={`today-color-${c.name}`}>
                  <div className="h-28" style={{ background: c.hex }} />
                  <div className="p-4">
                    <p className="font-medium">{c.name}</p>
                    <p className="text-sm text-ink/55 mt-1 leading-snug">{c.note}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* AI Style summary */}
        <section className="mt-16 bg-emerald text-white p-8 sm:p-12 rounded-[2px]" data-testid="style-summary-card">
          <div className="flex items-center gap-3 mb-5">
            <Wand2 className="w-5 h-5 text-gold" strokeWidth={1.5} />
            <p className="eyebrow text-gold">A note from your stylist</p>
          </div>
          {summary ? (
            <div className="space-y-4 font-serif text-xl sm:text-2xl leading-relaxed text-white/95">
              {summary.split("\n").filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
            </div>
          ) : (
            <div>
              <p className="text-lg text-white/80 max-w-xl">Let your stylist read your profile and write you a personal style note — grounded in your proportions, palette and the life you dress for.</p>
              <button onClick={generateSummary} disabled={genLoading || incomplete} data-testid="generate-summary-btn"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-emerald px-7 py-3 text-base hover:bg-white/90 transition-colors duration-300 disabled:opacity-50">
                {genLoading ? <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} /> : <Wand2 className="w-5 h-5" strokeWidth={1.5} />}
                {incomplete ? "Finish your profile first" : "Write my style note"}
              </button>
            </div>
          )}
        </section>

        {/* Shop my colors - full palette */}
        {palette?.tiers?.length > 0 && (
          <section className="mt-16">
            <div className="flex items-center gap-3 mb-2">
              <Palette className="w-5 h-5 text-emerald" strokeWidth={1.5} />
              <h2 className="text-3xl tracking-tight">Shop my colors</h2>
            </div>
            <p className="text-ink/60 mb-8">Your five-tier palette. We never ban a color — every tier includes how to wear it.</p>
            <div className="space-y-10">
              {palette.tiers.map((t) => (
                <div key={t.tier} data-testid={`palette-tier-${t.tier}`}>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="w-3 h-3 rounded-full" style={{ background: TIER_ACCENT[t.tier] }} />
                    <h3 className="text-2xl">{t.tier}</h3>
                  </div>
                  <p className="text-ink/60 text-sm mb-4">{t.blurb}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {t.colors.map((c) => (
                      <div key={c.name} className="border border-ink/10 rounded-[2px] overflow-hidden">
                        <div className="h-20" style={{ background: c.hex }} />
                        <div className="p-3">
                          <p className="font-medium text-sm">{c.name}</p>
                          <p className="text-xs text-ink/55 mt-1 leading-snug">{c.note}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Proportion insight */}
        {proportions?.body_type && (
          <section className="mt-16 border border-ink/10 p-8 rounded-[2px]" data-testid="proportion-card">
            <div className="flex items-center gap-3 mb-4">
              <Ruler className="w-5 h-5 text-emerald" strokeWidth={1.5} />
              <h2 className="text-3xl tracking-tight">Your proportions</h2>
            </div>
            <p className="text-lg leading-relaxed text-ink/80 max-w-2xl">{proportions.balance_note}</p>
            {proportions.ratios && Object.keys(proportions.ratios).length > 0 && (
              <div className="flex flex-wrap gap-8 mt-6">
                {Object.entries(proportions.ratios).map(([k, v]) => (
                  <div key={k}>
                    <p className="text-2xl font-serif">{v}</p>
                    <p className="text-xs uppercase tracking-widest text-ink/50 mt-1">{k.replace(/_/g, " ")}</p>
                  </div>
                ))}
              </div>
            )}
            <p className="text-sm text-ink/40 mt-6">Body-type language is an optional explanation, never a rule. You lead; we style around you.</p>
          </section>
        )}

        {/* Coming soon tiles */}
        <section className="mt-16 grid sm:grid-cols-2 gap-4">
          {[
            { icon: ShoppingBag, title: "Style Me", body: "Enter an occasion and receive complete, considered looks. Arriving soon." },
            { icon: Bookmark, title: "Saved Looks", body: "Keep your favourite looks in one place, filtered by budget. Arriving soon." },
          ].map((t) => (
            <div key={t.title} className="bg-[#F2EFEB] border border-ink/10 p-8 rounded-[2px]" data-testid={`soon-${t.title}`}>
              <t.icon className="w-6 h-6 text-emerald" strokeWidth={1.5} />
              <h3 className="text-2xl mt-4">{t.title}</h3>
              <p className="text-ink/60 mt-2">{t.body}</p>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
